import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { api, formatApiError, resolveMediaUrl } from "@/lib/api";
import { toast } from "sonner";

const DEFAULT = { x: 0.5, y: 0.5, scale: 1.0 };

function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }

function ensureCrop(c) {
  return {
    x: clamp(Number(c?.x ?? 0.5), 0, 1),
    y: clamp(Number(c?.y ?? 0.5), 0, 1),
    scale: clamp(Number(c?.scale ?? 1.0), 1, 3),
  };
}

/**
 * Modal that lets the studio set independent Desktop + Mobile crops for a single hero photo.
 * Live previews at 16:9 and 9:16. Click on preview to set X/Y focal point. Sliders adjust everything precisely.
 */
export function HeroCropModal({ photo, onClose, onSaved }) {
  const [desktop, setDesktop] = useState(() => ensureCrop(photo.hero_desktop));
  const [mobile, setMobile] = useState(() => ensureCrop(photo.hero_mobile || { ...DEFAULT, y: photo.orientation === "portrait" ? 0.3 : 0.5 }));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/photos/${photo.id}`, {
        hero_desktop: desktop,
        hero_mobile: mobile,
      });
      onSaved(data);
      toast.success("Hero framing saved.");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save.");
    } finally {
      setSaving(false);
    }
  }

  function resetOne(setter, y = 0.5) {
    setter({ x: 0.5, y, scale: 1.0 });
  }

  const src = resolveMediaUrl(photo.image_url);

  const modal = (
    <div
      data-testid="hero-crop-modal"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 999999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "clamp(16px, 3vw, 40px)",
        isolation: "isolate",
      }}
      onClick={onClose}
    >
      <div
        aria-hidden="true"
        style={{ position: "absolute", inset: 0, zIndex: 0, background: "rgba(15,12,11,0.92)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)" }}
      />
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          zIndex: 2,
          width: "100%",
          maxWidth: 1080,
          maxHeight: "90vh",
          overflowY: "auto",
          background: "#1A1514",
          border: "1px solid rgba(247,244,240,0.10)",
          padding: "clamp(20px, 3vw, 36px)",
        }}
      >
        <header className="flex items-center justify-between mb-6 md:mb-8">
          <div>
            <p className="text-[10px] tracking-[0.42em] uppercase text-ink-taupe">Hero Framing</p>
            <h2 className="mt-1 font-display text-2xl md:text-3xl text-ink-ivory">
              {photo.caption || "Untitled photograph"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="hero-crop-close"
            className="text-[10px] tracking-[0.32em] uppercase text-ink-ivory editorial-link"
          >
            Close ✕
          </button>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <CropPane
            label="Desktop · 16:9"
            aspect="16 / 9"
            src={src}
            crop={desktop}
            onChange={setDesktop}
            onReset={() => resetOne(setDesktop, 0.5)}
            testidPrefix="hero-crop-desktop"
          />
          <CropPane
            label="Mobile · 9:16"
            aspect="9 / 16"
            src={src}
            crop={mobile}
            onChange={setMobile}
            onReset={() => resetOne(setMobile, photo.orientation === "portrait" ? 0.3 : 0.5)}
            testidPrefix="hero-crop-mobile"
          />
        </div>

        <footer className="mt-8 flex items-center justify-between gap-4 flex-wrap">
          <p className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe">
            Click a preview to set the focal point · drag sliders for precision
          </p>
          <div className="flex items-center gap-6">
            <button
              type="button"
              onClick={onClose}
              data-testid="hero-crop-cancel"
              className="text-[10px] tracking-[0.32em] uppercase editorial-link"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="hero-crop-save"
              className="luxe-btn disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save framing"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(modal, document.body);
}

function CropPane({ label, aspect, src, crop, onChange, onReset, testidPrefix }) {
  const objectPosition = `${Math.round(crop.x * 100)}% ${Math.round(crop.y * 100)}%`;

  function handlePreviewClick(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = clamp((e.clientX - rect.left) / rect.width, 0, 1);
    const y = clamp((e.clientY - rect.top) / rect.height, 0, 1);
    onChange({ ...crop, x, y });
  }

  return (
    <section data-testid={testidPrefix} className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[10px] tracking-[0.32em] uppercase text-ink-champagne">{label}</p>
        <button
          type="button"
          onClick={onReset}
          data-testid={`${testidPrefix}-reset`}
          className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe editorial-link"
        >
          Reset
        </button>
      </div>

      <div
        onClick={handlePreviewClick}
        data-testid={`${testidPrefix}-preview`}
        style={{
          position: "relative",
          aspectRatio: aspect,
          background: "#0F0C0B",
          border: "1px solid rgba(247,244,240,0.10)",
          overflow: "hidden",
          cursor: "crosshair",
        }}
      >
        <img
          src={src}
          alt=""
          draggable={false}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition,
            transform: `scale(${crop.scale})`,
            transformOrigin: "center center",
            transition: "transform 220ms ease, object-position 220ms ease",
            pointerEvents: "none",
          }}
        />
        {/* Focal-point crosshair */}
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            left: `${crop.x * 100}%`,
            top: `${crop.y * 100}%`,
            width: 22,
            height: 22,
            marginLeft: -11,
            marginTop: -11,
            borderRadius: "50%",
            border: "1px solid rgba(226,211,193,0.9)",
            boxShadow: "0 0 0 1px rgba(15,12,11,0.6)",
            pointerEvents: "none",
          }}
        />
      </div>

      <Slider label="X" min={0} max={1} step={0.01}
        value={crop.x} onChange={(v) => onChange({ ...crop, x: v })}
        format={(v) => `${Math.round(v * 100)}%`}
        testid={`${testidPrefix}-x`}
      />
      <Slider label="Y" min={0} max={1} step={0.01}
        value={crop.y} onChange={(v) => onChange({ ...crop, y: v })}
        format={(v) => `${Math.round(v * 100)}%`}
        testid={`${testidPrefix}-y`}
      />
      <Slider label="Zoom" min={1} max={2.5} step={0.02}
        value={crop.scale} onChange={(v) => onChange({ ...crop, scale: v })}
        format={(v) => `${v.toFixed(2)}×`}
        testid={`${testidPrefix}-scale`}
      />
    </section>
  );
}

function Slider({ label, min, max, step, value, onChange, format, testid }) {
  return (
    <label className="block">
      <span className="flex items-center justify-between text-[10px] tracking-[0.32em] uppercase text-ink-taupe">
        <span>{label}</span>
        <span data-testid={`${testid}-value`} className="text-ink-champagne">{format(value)}</span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        data-testid={testid}
        className="mt-2 w-full accent-ink-champagne"
      />
    </label>
  );
}
